import { useMutation, useQuery, useQueryClient } from 'react-query';
import { z } from 'zod';

import { Defaults } from '../../constants';
import Api from '../api/api';

const waitlistEntrySchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  created: z.string().datetime(),
  invitedAt: z.string().datetime().nullable(),
  userId: z.string().uuid().nullable(),
  verified: z.boolean(),
  unavailable: z.boolean(),
});
const waitlistPageSchema = z.object({ entries: z.array(waitlistEntrySchema), total: z.number().int().nonnegative() });
export type WaitlistEntry = z.infer<typeof waitlistEntrySchema>;
export type WaitlistPage = z.infer<typeof waitlistPageSchema>;

export async function getWaitlist(page: number, search: string): Promise<WaitlistPage> {
  const [response, body] = await Api.get('waitlist', { page, search, limit: Defaults.LIST_PAGE_SIZE });
  if (!response.ok) throw body;
  const json: unknown = body;
  return waitlistPageSchema.parse(json);
}

export const useWaitlist = (page: number, search: string) => useQuery(['getWaitlist', page, search], () => getWaitlist(page, search));

export async function inviteWaitlistEntry(id: string): Promise<WaitlistEntry> {
  const [response, body] = await Api.post(`waitlist/${id}/invite`);
  if (!response.ok) throw body;
  const json: unknown = body;
  return waitlistEntrySchema.parse(json);
}

export const useInviteWaitlistEntry = () => {
  const client = useQueryClient();
  return useMutation('inviteWaitlistEntry', inviteWaitlistEntry, {
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries(['getWaitlist']),
        client.invalidateQueries(['getUsersByPage']),
        client.invalidateQueries(['getUsersCount']),
        client.invalidateQueries(['getUser']),
      ]),
  });
};

export const waitlistErrorSchema = z.object({ message: z.string() });
